import { FC, useMemo, useState } from "react";
import { LocationGroupType } from "@src/types.generated";
import useLocationGroups from "@src/features/locations/hooks/useLocationGroups";
import { pluralize } from "@src/lib/utils/strings";
import {
  locationGroupTypeDisplayName,
  locationGroupTypeDisplayNamePlural,
} from "@src/features/home/constants";
import Text from "@src/lib/ui/Text";
import { LocationGroupThinFragment } from "@src/features/locations/hooks/useLocationGroups.generated";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { Button } from "@src/lib/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import { Checkbox } from "@src/lib/ui/Checkbox";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";

type Props = {
  type: LocationGroupType;
  label?: string;
  placeholder?: string;
  locationGroupIds: string[];
  setLocationGroupIds: (x: string[]) => void;
  locationGroupOptions?: LocationGroupThinFragment[];
  dataTestId?: string;
};

export const LocationGroupMultiSelectField: FC<Props> = (props) => {
  const { lgTypeToLgs } = useLocationGroups();
  const [open, setOpen] = useState(false);
  const options = useMemo(() => {
    return (props.locationGroupOptions || lgTypeToLgs[props.type]).map((x) => ({
      value: x.id,
      label: x.name,
    }));
  }, [lgTypeToLgs, props.locationGroupOptions, props.type]);
  const label: string = useMemo(() => {
    if (props.label) return props.label;
    else {
      return pluralize(locationGroupTypeDisplayName[props.type], 2);
    }
  }, [props.label, props.type]);
  const selectedNames = useMemo(() => {
    return options
      .filter((o) => props.locationGroupIds.includes(o.value))
      .map((o) => o.label)
      .join(", ");
  }, [options, props.locationGroupIds]);
  return (
    <div className="flex flex-col gap-1 self-stretch">
      <div className="flex justify-between self-stretch">
        <Text variant="fine-print-title" className="text-black">
          {label}
        </Text>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex w-full justify-between px-3"
            data-testid={props.dataTestId || "multi-select-field-input"}
          >
            <span
              className={selectedNames ? "truncate" : "truncate text-gray-5"}
            >
              {selectedNames ||
                props.placeholder ||
                `Select ${locationGroupTypeDisplayNamePlural[
                  props.type
                ].toLowerCase()}...`}
            </span>
            <ChevronDownIcon className="ml-2 size-4 shrink-0 text-gray-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          disablePortal
        >
          <Command>
            <CommandInput placeholder="Search..." className="h-9" />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isChecked = props.locationGroupIds.includes(
                    option.value,
                  );
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      data-testid="multi-select-option"
                      onSelect={() => {
                        if (isChecked) {
                          props.setLocationGroupIds(
                            props.locationGroupIds.filter(
                              (id) => id !== option.value,
                            ),
                          );
                        } else {
                          props.setLocationGroupIds([
                            ...props.locationGroupIds,
                            option.value,
                          ]);
                        }
                        setOpen(true);
                      }}
                      className="gap-2"
                    >
                      <Checkbox checked={isChecked} />
                      <Text variant="compact-description">{option.label}</Text>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default LocationGroupMultiSelectField;
